import { useAccount, useReadContract, useReadContracts, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { useEffect, useRef } from "react";
import type { Address, Hex } from "viem";
import { stringToHex } from "viem";
import { REFERRAL_REGISTRY_ABI } from "@/lib/abis";
import { ADDRESSES } from "@/lib/config";

const REF_STORAGE_KEY = "safepiggy:pendingRef";

/// Read `?ref=<code>` from the URL on first load and persist to sessionStorage
/// so it survives wallet-connect navigation. Returns the code if any is
/// present. sessionStorage > localStorage on purpose: a stale ref param
/// shouldn't linger between browser sessions.
export function readPendingRef(): string | null {
    if (typeof window === "undefined") return null;
    try {
        const fromUrl = new URLSearchParams(window.location.search).get("ref");
        if (fromUrl) {
            const cleaned = fromUrl.trim().slice(0, 12);
            // basic sanity — codes are alnum + dash
            if (/^[A-Za-z0-9_-]+$/.test(cleaned)) {
                window.sessionStorage.setItem(REF_STORAGE_KEY, cleaned);
                return cleaned;
            }
        }
        return window.sessionStorage.getItem(REF_STORAGE_KEY);
    } catch {
        return null;
    }
}

export function clearPendingRef() {
    if (typeof window === "undefined") return;
    try { window.sessionStorage.removeItem(REF_STORAGE_KEY); } catch { /* ignore */ }
}

const ZERO_BYTES12: Hex = "0x000000000000000000000000";
const ZERO_ADDR: Address = "0x0000000000000000000000000000000000000000";

export const codeToBytes12 = (code: string): Hex => {
    const trimmed = code.trim().slice(0, 12);
    return stringToHex(trimmed, { size: 12 });
};

export const bytes12ToString = (hex: Hex): string => {
    const stripped = hex.startsWith("0x") ? hex.slice(2) : hex;
    let s = "";
    for (let i = 0; i < stripped.length; i += 2) {
        const byte = parseInt(stripped.slice(i, i + 2), 16);
        if (byte === 0) break;
        s += String.fromCharCode(byte);
    }
    return s;
};

export interface ReferralData {
    myCode: Hex;
    myCodeString: string;
    volume: bigint;
    buyCount: bigint;
    earned: bigint;
    multiplierBps: number;
    attributedTo: Address;
}

const DEFAULTS: ReferralData = {
    myCode: ZERO_BYTES12,
    myCodeString: "",
    volume: 0n,
    buyCount: 0n,
    earned: 0n,
    multiplierBps: 10_000,
    attributedTo: ZERO_ADDR,
};

export function useReferralData(): { data: ReferralData; live: boolean; refetch: () => void } {
    const { address } = useAccount();
    const contract = ADDRESSES.REFERRAL_REGISTRY;
    const targetAddr: Address = address ?? ZERO_ADDR;

    const reads = useReadContracts({
        allowFailure: true,
        contracts: contract
            ? [
                  { address: contract, abi: REFERRAL_REGISTRY_ABI, functionName: "referrerCode",   args: [targetAddr] } as const,
                  { address: contract, abi: REFERRAL_REGISTRY_ABI, functionName: "referrerVolume", args: [targetAddr] } as const,
                  { address: contract, abi: REFERRAL_REGISTRY_ABI, functionName: "referrerBuyCount", args: [targetAddr] } as const,
                  { address: contract, abi: REFERRAL_REGISTRY_ABI, functionName: "earned",         args: [targetAddr] } as const,
                  { address: contract, abi: REFERRAL_REGISTRY_ABI, functionName: "previewReferralMultiplier", args: [targetAddr] } as const,
                  { address: contract, abi: REFERRAL_REGISTRY_ABI, functionName: "attributionOf",  args: [targetAddr] } as const,
              ]
            : undefined,
        query: { enabled: Boolean(contract && address), refetchInterval: 15_000 },
    });

    if (!contract || !address || !reads.data) {
        return { data: DEFAULTS, live: false, refetch: () => reads.refetch() };
    }

    const myCode = (reads.data[0]?.result as Hex | undefined) ?? ZERO_BYTES12;

    return {
        data: {
            myCode,
            myCodeString: bytes12ToString(myCode),
            volume:        (reads.data[1]?.result as bigint | undefined) ?? 0n,
            buyCount:      (reads.data[2]?.result as bigint | undefined) ?? 0n,
            earned:        (reads.data[3]?.result as bigint | undefined) ?? 0n,
            multiplierBps: Number((reads.data[4]?.result as number | undefined) ?? 10_000),
            attributedTo:  (reads.data[5]?.result as Address | undefined) ?? ZERO_ADDR,
        },
        live: true,
        refetch: () => reads.refetch(),
    };
}

export function useRegisterCode() {
    const contract = ADDRESSES.REFERRAL_REGISTRY;
    const { writeContract, data: hash, isPending, reset } = useWriteContract();
    const { isLoading: confirming, isSuccess: confirmed } = useWaitForTransactionReceipt({ hash });

    useEffect(() => {
        if (confirmed) {
            const t = setTimeout(reset, 4_000);
            return () => clearTimeout(t);
        }
    }, [confirmed, reset]);

    const register = (codeText: string) => {
        if (!contract) return;
        writeContract({
            address: contract,
            abi: REFERRAL_REGISTRY_ABI,
            functionName: "registerCode",
            args: [codeToBytes12(codeText)],
        });
    };

    return {
        register,
        hash,
        status: confirmed ? "confirmed" : confirming ? "confirming" : isPending ? "pending" : "idle",
    } as const;
}

/**
 * C2 fix: read `?ref=<code>` from URL, persist to sessionStorage, and call
 * `attribute(code)` once the user connects — but only if they are NOT
 * already attributed (one-shot). Mounted once at the app root.
 */
export function useAutoAttribute() {
    const { address, isConnected } = useAccount();
    const contract = ADDRESSES.REFERRAL_REGISTRY;
    const { writeContract } = useWriteContract();
    const submitted = useRef(false);

    const targetAddr: Address = address ?? ZERO_ADDR;
    const { data: attributedTo } = useReadContract({
        address: contract,
        abi: REFERRAL_REGISTRY_ABI,
        functionName: "attributionOf",
        args: [targetAddr],
        query: { enabled: Boolean(contract && address) },
    });

    useEffect(() => {
        if (submitted.current) return;
        if (!isConnected || !address || !contract) return;
        if (attributedTo === undefined) return; // still loading
        if (attributedTo && attributedTo !== ZERO_ADDR) {
            // already attributed — clear any pending ref and stop
            clearPendingRef();
            submitted.current = true;
            return;
        }
        const code = readPendingRef();
        if (!code) return;
        submitted.current = true;
        try {
            writeContract({
                address: contract,
                abi: REFERRAL_REGISTRY_ABI,
                functionName: "attribute",
                args: [codeToBytes12(code)],
            });
        } catch {
            // ignore — user may reject; we don't retry to avoid prompt spam
        } finally {
            // Clear the pending ref whether the tx succeeds or the user
            // rejects — they explicitly opted out and a stale code should
            // not reappear on the next reload.
            clearPendingRef();
        }
    }, [isConnected, address, contract, attributedTo, writeContract]);
}

export function useClaimReferralRewards() {
    const contract = ADDRESSES.REFERRAL_REGISTRY;
    const { writeContract, data: hash, isPending, reset } = useWriteContract();
    const { isLoading: confirming, isSuccess: confirmed } = useWaitForTransactionReceipt({ hash });

    useEffect(() => {
        if (confirmed) {
            const t = setTimeout(reset, 4_000);
            return () => clearTimeout(t);
        }
    }, [confirmed, reset]);

    const claim = () => {
        if (!contract) return;
        writeContract({ address: contract, abi: REFERRAL_REGISTRY_ABI, functionName: "claim", args: [] });
    };

    return {
        claim,
        hash,
        status: confirmed ? "confirmed" : confirming ? "confirming" : isPending ? "pending" : "idle",
    } as const;
}
